import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSessionToken, useDescope } from "@descope/react-sdk";
import axios from "axios";
import jwtDecode from "jwt-decode";

import apiRoutes from "../constants/api-routes";
import pages from "../constants/pages";
import { resetNavigationState } from "../redux/navigationSlice";
import {
	selectUser,
	setDescopeUserId,
	setEmail,
	setFirstName,
	setGroups,
	setIsCoAppSupport,
	setLastName,
	setOrganizationForceLogin,
	setOrganizationIdentityProvider,
	setOrganizationKillSwitch,
	setOrganizationName,
	setOrganizationSsoAndScim,
	setOrgId,
	setPermissions,
	setPhone,
	setRoles,
	setUserId
} from "../redux/userSlice";

/**
 * Custom hook to manage user session and automatically refresh user data
 * - Refreshes user data every hour
 * - Validates session token and auto-logs out if expired
 * - Handles session expiration
 * 
 * In the future, we could add a session expiration alert before logging them out
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.refreshInterval - Interval for refreshing user data in ms (default: 1 hour)
 * @param {number} options.sessionCheckInterval - Interval for session validation in ms (default: 5 minutes)
 */
const useSessionManager = (options = {}) => {
	const {
		refreshInterval = 300000, // 5 minutes
		sessionCheckInterval = 300000 // 5 minutes
	} = options;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { logout } = useDescope();
	const user = useSelector(selectUser);
	
	
	const refreshIntervalRef = useRef(null);
	const sessionCheckIntervalRef = useRef(null);

	
	const logoutUser = useCallback(async () => {
		try {
			await logout();
		} catch (e) {
			console.error("Error during logout:", e);
		}

	
		dispatch(setUserId(null));
		dispatch(setEmail(null));
		dispatch(setFirstName(null));
		dispatch(setGroups([]));
		dispatch(setLastName(null));
		dispatch(setPermissions([]));
		dispatch(setPhone(null));
		dispatch(setRoles([]));
		dispatch(setOrgId(null));
		dispatch(setIsCoAppSupport(false));
		dispatch(setOrganizationName(null));
		dispatch(setOrganizationForceLogin(null));
		dispatch(setOrganizationKillSwitch(null));
		dispatch(setOrganizationSsoAndScim(null));
		dispatch(setOrganizationIdentityProvider(null));
		dispatch(setDescopeUserId(null));
		dispatch(resetNavigationState());
		
	
		if (refreshIntervalRef.current) {
			clearInterval(refreshIntervalRef.current);
			refreshIntervalRef.current = null;
		}
		if (sessionCheckIntervalRef.current) {
			clearInterval(sessionCheckIntervalRef.current);
			sessionCheckIntervalRef.current = null;
		}
		
		navigate(pages.login);
	}, [dispatch, logout, navigate]);

	const isTokenExpired = useCallback((token) => {
		if (!token) return true;
		
		try {
			const decoded = jwtDecode(token);
			const currentTime = Date.now() / 1000;
			
			return decoded.exp < (currentTime + 300);
		} catch (error) {
			console.error("Error decoding token:", error);
			return true;
		}
	}, []);

	const refreshUserData = useCallback(async () => {
		const sessionToken = getSessionToken();
		
		if (!sessionToken || !user.email) {
			console.warn("No session token or user email available for refresh");
			return;
		}

		if (isTokenExpired(sessionToken)) {
			console.warn("Session token expired, logging out user");
			logoutUser();
			return;
		}

		try {
			const decodedJwt = jwtDecode(sessionToken);
			const tenantId = decodedJwt.tenantId || null;
			
			const response = await axios.get(
				apiRoutes.getUser(1, "", user.email, tenantId),
				{ headers: { "Authorization": "Bearer " + sessionToken } }
			);

			dispatch(setFirstName(response.data.firstname));
			dispatch(setLastName(response.data.lastname));
			dispatch(setGroups(response.data.groups));
			dispatch(setRoles(response.data.roles));
			dispatch(setPermissions(response.data.permissions));
			dispatch(setIsCoAppSupport(response.data.isCoAppSupport));
			dispatch(setPhone(response.data.phone));
			dispatch(setOrgId(response.data.organizationId));
			dispatch(setOrganizationName(response.data.organization?.name));
			
			if (response.data.organization) {
				dispatch(setOrganizationForceLogin(response.data.organization.forceLoginActive));
				dispatch(setOrganizationKillSwitch(response.data.organization.killSwitchActive));
				dispatch(setOrganizationSsoAndScim(response.data.organization.isSsoAndScimEnabled));
				if (response.data.organization.isSsoAndScimEnabled) {
					dispatch(setOrganizationIdentityProvider(response.data.organization.identityProviderName));
				}
			}
			
		} catch (error) {
			logoutUser();
		}
	}, [user.email, dispatch, isTokenExpired, logoutUser]);

	const validateSession = useCallback(() => {
		const sessionToken = getSessionToken();
		
		if (!sessionToken) {
			console.warn("No session token found, logging out user");
			logoutUser();
			return;
		}

		if (isTokenExpired(sessionToken)) {
			console.warn("Session token expired during validation, logging out user");
			logoutUser();
		}
	}, [isTokenExpired, logoutUser]);

	useEffect(() => {
		if (user.userId && user.email) {
			refreshIntervalRef.current = setInterval(refreshUserData, refreshInterval);
			sessionCheckIntervalRef.current = setInterval(validateSession, sessionCheckInterval);
		}

		return () => {
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
				refreshIntervalRef.current = null;
			}
			if (sessionCheckIntervalRef.current) {
				clearInterval(sessionCheckIntervalRef.current);
				sessionCheckIntervalRef.current = null;
			}
		};
	}, [user.userId, user.email, refreshUserData, validateSession, refreshInterval, sessionCheckInterval]);
};

export default useSessionManager;
